import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ACTIVE_LEAD_STATUSES,
  Input,
  Select,
  StatsBreakdownTable,
  StatsSummary,
  StatusTimeseriesChart,
  cn,
  type LeadStatus,
  type TimeseriesBucket,
} from "@toss-ads/ui";
import {
  fetchAdminStatusBreakdown,
  fetchAdminContactTime,
  fetchAdminStatusTimeseries,
  fetchAssignees,
} from "../lib/api";
import { useUrlState } from "../lib/use-url-state";

type Preset = "recent-7d" | "recent-30d" | "recent-90d" | "all" | "custom";

function rangeFor(
  preset: Preset,
  customFrom: string,
  customTo: string,
): { from?: string; to?: string } {
  if (preset === "all") return {};
  if (preset === "custom") {
    return {
      from: customFrom
        ? new Date(`${customFrom}T00:00:00+09:00`).toISOString()
        : undefined,
      to: customTo
        ? new Date(`${customTo}T23:59:59.999+09:00`).toISOString()
        : undefined,
    };
  }
  const days = preset === "recent-7d" ? 7 : preset === "recent-90d" ? 90 : 30;
  const now = new Date();
  const from = new Date(now.getTime() - days * 24 * 3600 * 1000);
  return { from: from.toISOString(), to: now.toISOString() };
}

export function AdvertiserStatsSection({
  advertiserId,
}: {
  advertiserId: string;
}) {
  // 통계 탭 필터는 URL 에 "s." 접두어로 (리드 탭 파라미터와 충돌 방지).
  const [presetRaw, setPreset] = useUrlState("s.date", "recent-30d");
  const preset = presetRaw as Preset;
  const [customFrom, setCustomFrom] = useUrlState("s.from", "");
  const [customTo, setCustomTo] = useUrlState("s.to", "");
  const [assignee, setAssignee] = useUrlState("s.assignee", "");
  const [bucketRaw, setBucket] = useUrlState("s.bucket", "day");
  const bucket = bucketRaw as TimeseriesBucket;
  const [statusesStr, setStatusesStr] = useUrlState("s.statuses", "");
  const total = ACTIVE_LEAD_STATUSES.length;
  const visible = React.useMemo<Set<LeadStatus>>(() => {
    if (statusesStr === "")
      return new Set(ACTIVE_LEAD_STATUSES.map((m) => m.value));
    if (statusesStr === "none") return new Set();
    return new Set(statusesStr.split(",").filter(Boolean) as LeadStatus[]);
  }, [statusesStr]);
  const setVisibleSet = React.useCallback(
    (next: Set<LeadStatus>) => {
      if (next.size === total) setStatusesStr("");
      else if (next.size === 0) setStatusesStr("none");
      else setStatusesStr(Array.from(next).join(","));
    },
    [total, setStatusesStr],
  );

  const range = React.useMemo(
    () => rangeFor(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  const { data: assignees } = useQuery({
    queryKey: ["admin", "assignees", advertiserId],
    queryFn: () => fetchAssignees(advertiserId),
  });

  const { data: timeseries, isFetching } = useQuery({
    queryKey: ["admin", "timeseries", advertiserId, range, bucket, assignee],
    queryFn: () =>
      fetchAdminStatusTimeseries(advertiserId, {
        from: range.from,
        to: range.to,
        bucket,
        assignee: assignee || undefined,
      }),
  });

  const { data: byAssignee } = useQuery({
    queryKey: ["admin", "breakdown", advertiserId, range, "assignee"],
    queryFn: () =>
      fetchAdminStatusBreakdown(advertiserId, {
        from: range.from,
        to: range.to,
        group: "assignee",
      }),
  });
  const { data: byMedia } = useQuery({
    queryKey: ["admin", "breakdown", advertiserId, range, "media"],
    queryFn: () =>
      fetchAdminStatusBreakdown(advertiserId, {
        from: range.from,
        to: range.to,
        group: "media",
      }),
  });
  const { data: contactTime } = useQuery({
    queryKey: ["admin", "contact-time", advertiserId, range],
    queryFn: () =>
      fetchAdminContactTime(advertiserId, { from: range.from, to: range.to }),
  });

  function toggle(v: LeadStatus) {
    const next = new Set(visible);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setVisibleSet(next);
  }
  const allOn = visible.size === total;

  return (
    <div>
      <div className="rounded-lg border bg-surface p-5 mb-3">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Select
            value={preset}
            onChange={(e) => setPreset(e.target.value as Preset)}
          >
            <option value="recent-7d">최근 7일</option>
            <option value="recent-30d">최근 30일</option>
            <option value="recent-90d">최근 90일</option>
            <option value="all">전체 기간</option>
            <option value="custom">날짜 지정</option>
          </Select>
          {preset === "custom" && (
            <>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-[160px] h-[34px]"
              />
              <span className="text-[12px] text-ink-subtle">~</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-[160px] h-[34px]"
              />
            </>
          )}
          <Select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">담당자 전체</option>
            <option value="unassigned">미배정</option>
            {(assignees ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.note || a.email}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() =>
              setVisibleSet(
                allOn
                  ? new Set()
                  : new Set(ACTIVE_LEAD_STATUSES.map((m) => m.value)),
              )
            }
            className="px-2 py-1 text-[11px] rounded-full border border-border text-ink-muted hover:bg-surface-muted"
          >
            {allOn ? "전체 해제" : "전체 선택"}
          </button>
          {ACTIVE_LEAD_STATUSES.map((m) => {
            const on = visible.has(m.value);
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => toggle(m.value)}
                className={cn(
                  "inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-full border transition-colors",
                  on
                    ? "border-transparent text-white"
                    : "border-border text-ink-subtle bg-surface hover:bg-surface-muted",
                )}
                style={on ? { backgroundColor: m.color } : undefined}
              >
                {!on && (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: m.color }}
                  />
                )}
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border bg-surface p-5 mb-3">
        <StatsSummary
          rows={byAssignee ?? []}
          avgContactSeconds={contactTime?.avgSeconds ?? null}
          contactSampleCount={contactTime?.sampleCount ?? 0}
        />
      </div>

      <div className="rounded-lg border bg-surface p-5 mb-3">
        <StatusTimeseriesChart
          data={timeseries ?? []}
          bucket={bucket}
          onBucketChange={setBucket}
          loading={isFetching}
          visibleStatuses={Array.from(visible)}
        />
      </div>

      <div className="grid gap-3">
        <div className="rounded-lg border bg-surface p-5">
          <StatsBreakdownTable
            title="직원별 처리 현황"
            firstColHeader="담당자"
            rows={byAssignee ?? []}
            showColorDot
            showAttempts
          />
        </div>
        <div className="rounded-lg border bg-surface p-5">
          <StatsBreakdownTable
            title="매체별 처리 현황"
            firstColHeader="매체"
            rows={byMedia ?? []}
          />
        </div>
      </div>
    </div>
  );
}
